import { StateCreator } from 'zustand';
import { isEqual } from 'lodash';
import { RootState } from '../rootStore';
import { Market, MarketID, MarketState, MarketConfig, FundingEvent } from '@/api/spec';
import { MarketData } from '@/common/MarketData.ts';
import {
  mapMarkets,
  updateMarketDataWithStats,
  updateMarketDataWithConfig,
  updateMarketDataWithFunding,
} from '../../features/markets/utils/mapMarketData.ts';
import { Exchange } from '../../types/Exchange.ts';
import { schedulePositionRecalcForMarkets } from '../utils/positionRecalcScheduler';

const hasMarketChanges = (market: MarketData, updates: Partial<MarketData>): boolean => {
  return Object.keys(updates).some((key) => {
    const typedKey = key as keyof MarketData;
    return !isEqual(market[typedKey], updates[typedKey]);
  });
};
export const findMarketIdBySymbol = (markets: Map<number, MarketData>, symbol: string | undefined): MarketID | undefined => {
  if (!symbol) return undefined;
  const searchSymbol = symbol.toUpperCase();
  for (const [id, market] of markets.entries()) {
    if (market.symbol?.toUpperCase() === searchSymbol) {
      return id;
    }
  }
  return undefined;
};

export type FavoritesDisplayMode = 'percent' | 'price';

export interface MarketSlice {
  selectedMarketId?: MarketID;
  markets: Map<number, MarketData>;
  favoriteMarkets: MarketID[];
  favoritesDisplayMode: FavoritesDisplayMode;

  // Actions
  selectMarket: (marketId: MarketID) => void;
  initializeMarkets: (markets: Market[], exchange: Exchange) => void;
  updateMarketStats: (marketId: MarketID, stats: MarketState) => void;
  updateMarketsStats: (stats: Record<MarketID, MarketState | undefined>) => void;
  updateMarketConfig: (marketId: MarketID, config: MarketConfig | undefined) => void;
  updateMarketFunding: (marketId: MarketID, funding: FundingEvent) => void;
  updateMarketsFunding: (funding: Record<MarketID, FundingEvent | undefined>) => void;
  applyFundingUpdate: (headBlock: number, marketId: MarketID) => void;
  toggleFavoriteMarket: (marketId: MarketID) => void;
  setFavoritesDisplayMode: (mode: FavoritesDisplayMode) => void;
  // Selectors
  getSelectedMarket: () => MarketData | undefined;
  getMarketById: (id: MarketID) => MarketData | undefined;
}

export const createMarketSlice: StateCreator<RootState, [['zustand/immer', never]], [], MarketSlice> = (set, get) => ({
  markets: new Map<number, MarketData>(),
  favoriteMarkets: [],
  favoritesDisplayMode: 'percent',

  selectMarket: (marketId: MarketID) => {
    if (get().markets.has(marketId)) {
      set((state) => {
        state.selectedMarketId = marketId;
      });
    }
  },

  initializeMarkets: (markets: Market[], exchange: Exchange) => {
    const marketsMap = mapMarkets(markets, exchange, get().favoriteMarkets);

    set((state) => {
      state.markets = marketsMap;
    });
  },

  updateMarketStats: (marketId: MarketID, stats: MarketState) => {
    get().updateMarketsStats({ [marketId]: stats });
  },

  updateMarketsStats: (statsUpdate: Record<MarketID, MarketState | undefined>) => {
    const markPriceChangedMarketIds: number[] = [];
    set((state) => {
      let hasChanges = false;

      for (const marketIdStr in statsUpdate) {
        const marketId = Number(marketIdStr);
        const stats = statsUpdate[marketId];
        const market = state.markets.get(marketId);

        if (!market || !stats) continue;

        const previousMarkPrice = market.markPrice;
        const updatedProperties = updateMarketDataWithStats(market, stats);

        if (updatedProperties.markPrice != null && updatedProperties.markPrice !== previousMarkPrice) {
          markPriceChangedMarketIds.push(marketId);
        }

        if (hasMarketChanges(market, updatedProperties)) {
          hasChanges = true;
          state.markets.set(marketId, { ...market, ...updatedProperties });
        }
      }

      return hasChanges ? undefined : state;
    });

    if (markPriceChangedMarketIds.length > 0) {
      schedulePositionRecalcForMarkets(markPriceChangedMarketIds);
    }
  },

  updateMarketConfig: (marketId: MarketID, config?: MarketConfig) => {
    set((state) => {
      const market = state.markets.get(marketId);
      if (!market || !config) return;

      const updatedProperties = updateMarketDataWithConfig(market, config);

      if (hasMarketChanges(market, updatedProperties)) {
        state.markets.set(marketId, { ...market, ...updatedProperties });
      } else {
        return state;
      }
    });
  },

  updateMarketFunding: (marketId: MarketID, funding: FundingEvent) => {
    get().updateMarketsFunding({ [marketId]: funding });
  },

  // This call regularly happens a minute before actual funding event (block) runs
  updateMarketsFunding: (fundingUpdate: Record<MarketID, FundingEvent | undefined>) => {
    set((state) => {
      let hasChanges = false;

      for (const marketIdStr in fundingUpdate) {
        const marketId = Number(marketIdStr);
        const funding = fundingUpdate[marketId];
        const market = state.markets.get(marketId);

        if (!market || !funding) continue;

        const updatedProperties = {
          upcomingFundingEvent: funding,
          nextFundingBlock: funding.feb,
        };

        if (hasMarketChanges(market, updatedProperties)) {
          hasChanges = true;
          state.markets.set(marketId, { ...market, ...updatedProperties });
        }
      }

      return hasChanges ? undefined : state;
    });
  },

  applyFundingUpdate: (headBlock: number, marketId: MarketID) => {
    set((state) => {
      const market = state.markets.get(marketId);
      if (!market?.upcomingFundingEvent) {
        return;
      }

      const updatedProperties = updateMarketDataWithFunding(market, market.upcomingFundingEvent, headBlock);
      Object.assign(market, updatedProperties);
      market.upcomingFundingEvent = undefined;
      schedulePositionRecalcForMarkets([marketId]);
    });
  },

  toggleFavoriteMarket: (marketId: MarketID) => {
    set((state) => {
      const isFavorite = state.favoriteMarkets.includes(marketId);
      const market = state.markets.get(marketId);

      state.favoriteMarkets = isFavorite
        ? state.favoriteMarkets.filter((id) => id !== marketId)
        : [...state.favoriteMarkets, marketId];

      if (market) {
        state.markets.set(marketId, { ...market, isFavorite: !isFavorite });
      }
    });
  },

  setFavoritesDisplayMode: (mode: FavoritesDisplayMode) => {
    set((state) => {
      state.favoritesDisplayMode = mode;
    });
  },

  getSelectedMarket: () => {
    const { selectedMarketId, markets } = get();
    return selectedMarketId ? markets.get(selectedMarketId) : undefined;
  },

  getMarketById: (id: MarketID) => get().markets.get(id),
});
